import { useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { useExerciseLibrary } from '@/hooks/useExerciseLibrary'
import { parseWorkbook, countExercises } from '@/lib/parseProgramSheet'
import type { Program } from '@/types'

/**
 * Import on-ramp: bring a program in from a spreadsheet (Excel / Google Sheets
 * exported to .xlsx or .csv) or by pasting from a Claude chat. Both routes
 * normalise to the app's Program format and hand off to the same editable
 * preview as the AI + manual paths.
 *
 * For this preview: pasting our JSON format loads instantly. Spreadsheet
 * parsing + free-text AI normalisation run server-side (Phase 5) — the file
 * picker is wired and validates the file, then explains the next step.
 */
export default function ImportPanel({ onLoaded }: { onLoaded: (p: Program) => void }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const { resolveId } = useExerciseLibrary()
  const [paste, setPaste] = useState('')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<{ kind: 'ok' | 'err' | 'info'; msg: string } | null>(null)

  const tryPaste = () => {
    const text = paste.trim()
    if (!text) return
    // If it's already our JSON program format, load it straight into the editor.
    try {
      const obj = JSON.parse(text)
      if (obj && typeof obj === 'object' && Array.isArray(obj.weeks)) {
        onLoaded({ id: 'imported-' + Date.now(), source: 'import', name: obj.name ?? 'Imported Program', ...obj })
        setStatus({ kind: 'ok', msg: 'Program loaded into the editor below.' })
        return
      }
    } catch {
      /* not JSON — free text */
    }
    // Free-text program → needs the server-side AI normaliser (Phase 5).
    setStatus({
      kind: 'info',
      msg: 'Looks like free-text. On the live app this is sent to Claude to convert into the program format, then dropped into the editor. (Server step — coming next.)',
    })
  }

  const onFile = async (file: File) => {
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      setStatus({ kind: 'err', msg: 'Please choose an .xlsx, .xls or .csv file.' })
      return
    }
    setBusy(true)
    setStatus(null)
    try {
      const XLSX = await import('xlsx')
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf, { type: 'array' })
      const sheets = wb.SheetNames.map((n) => ({
        name: n,
        rows: XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, defval: '', blankrows: false }) as unknown[][],
      }))
      const name = file.name.replace(/\.(xlsx|xls|csv)$/i, '').replace(/[-_]+/g, ' ')
      const program = parseWorkbook(sheets, { name, resolveId })
      const n = countExercises(program)
      if (n === 0) {
        setStatus({
          kind: 'err',
          msg: 'No exercises found. Make sure the sheet has a header row with an "Exercise" column (and optionally Week, Day, Sets, Reps, Load, Notes).',
        })
        return
      }
      onLoaded(program)
      setStatus({ kind: 'ok', msg: `Loaded ${n} exercise${n === 1 ? '' : 's'} — review and edit below.` })
    } catch (e) {
      setStatus({ kind: 'err', msg: 'Could not read that file: ' + (e as Error).message })
    } finally {
      setBusy(false)
    }
  }

  const downloadTemplate = async () => {
    const XLSX = await import('xlsx')
    const rows = [
      ['Week', 'Day', 'Group', 'Exercise', 'Sets', 'Reps', 'Load', 'Tempo', 'Rest', 'Notes'],
      [1, 'Day 1', 'A1', 'Back Squat', 4, '6', '80% 1RM', '3-1-1', '3 min', 'Brace hard, drive through mid-foot'],
      [1, 'Day 1', 'A2', 'Seated Calf Raise', 4, '10', 'heavy', '', '90s', ''],
      [1, 'Day 1', 'B1', 'DB Bench Press', 4, '8', '', '', '2 min', ''],
      [1, 'Day 2', '', 'Romanian Deadlift', 4, '8', 'RPE 8', '3-0-1', '2 min', 'Hamstring length under load'],
      [2, 'Day 1', 'A1', 'Back Squat', 4, '5', '82.5% 1RM', '', '3 min', 'Add load if last week felt good'],
    ]
    const ws = XLSX.utils.aoa_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Program')
    XLSX.writeFile(wb, 'from-zero-program-template.xlsx')
  }

  return (
    <div className="animate-fade-in space-y-8">
      {/* Spreadsheet upload */}
      <section>
        <h3 className="font-condensed text-sm font-bold uppercase tracking-[0.15em] text-text">
          From a spreadsheet
        </h3>
        <p className="mb-3 mt-1 text-sm text-muted">
          Excel or Google Sheets (export as .xlsx or .csv). One exercise per row, with a header row
          naming the columns —{' '}
          <button onClick={downloadTemplate} className="text-gold underline underline-offset-2 hover:text-gold-bright">
            download a template
          </button>
          .
        </p>
        <div
          onClick={() => !busy && fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            const f = e.dataTransfer.files?.[0]
            if (f) onFile(f)
          }}
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed border-border-strong bg-surface px-6 py-10 text-center transition-colors hover:border-gold/60"
        >
          <span className="text-3xl">{busy ? '⏳' : '⬆'}</span>
          <p className="font-condensed text-lg font-bold">
            {busy ? 'Reading…' : 'Drop a file or click to browse'}
          </p>
          <p className="text-xs text-subtle">.xlsx · .xls · .csv</p>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          />
        </div>
      </section>

      {/* Paste from Claude */}
      <section>
        <h3 className="font-condensed text-sm font-bold uppercase tracking-[0.15em] text-text">
          Paste from Claude
        </h3>
        <p className="mb-3 mt-1 text-sm text-muted">
          Built it in a Claude chat? Paste it here — the app normalises it into an editable program.
        </p>
        <textarea
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          placeholder="Paste the program (text, or the app's JSON format)…"
          className="h-40 w-full resize-none rounded-input border border-border-strong bg-white/[.03] p-4 text-sm leading-relaxed text-text outline-none placeholder:text-subtle focus:border-gold focus:ring-2 focus:ring-gold/25"
        />
        <Button className="mt-3" disabled={!paste.trim()} onClick={tryPaste}>
          Load into editor
        </Button>
      </section>

      {status && (
        <p
          className={
            'rounded-btn border px-4 py-3 text-sm ' +
            (status.kind === 'ok'
              ? 'border-success/30 bg-success/10 text-success'
              : status.kind === 'err'
              ? 'border-danger/30 bg-danger/10 text-danger'
              : 'border-data/30 bg-data/10 text-data')
          }
        >
          {status.msg}
        </p>
      )}
    </div>
  )
}
